import React, { useState, useEffect } from 'react'
import { Modal, Input, Select, DatePicker } from 'antd'
import dayjs from 'dayjs'

const SearchAndFilterBar = ({
  columns,
  search,
  setSearch,
  filters,
  setFilters,
  setPagination
}) => {
  const [open, setOpen] = useState(false)

  const [tempSearch, setTempSearch] = useState(search || '')
  const [tempFilters, setTempFilters] = useState(filters || {})

  useEffect(() => {
    setTempSearch(search || '')
  }, [search])

  useEffect(() => {
    setTempFilters(filters || {})
  }, [filters])

  const handleChange = (field, value) => {
    const updated = { ...tempFilters, [field]: value }

    // Clear dependent filters when the parent filter is cleared
    if (!value) {
      columns.forEach(col => {
        if (col.related === field) {
          updated[col.dataIndex] = undefined
        }
      })
    }

    setTempFilters(updated)
  }

  const handleApplyFilters = () => {
    const cleaned = Object.fromEntries(
      Object.entries(tempFilters).filter(([_, v]) => v !== '' && v != null)
    )
    setFilters(cleaned)
    setPagination(prev => ({ ...prev, current: 1 }))
    setOpen(false)
  }

  const handleSearch = () => {
    setSearch(tempSearch)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleReset = () => {
    setSearch('')
    setFilters({})
    setTempSearch('')
    setTempFilters({})
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const renderField = col => {
    if (col.filterComponent) {
      // Check if component has dependency and should be hidden
      if (col.relatedDependence && col.related && !tempFilters[col.related]) {
        return null // Hide component if parent filter is not selected
      }

      const Component = col.filterComponent
      const props = {
        value: tempFilters[col.dataIndex] || '',
        onChange: val => handleChange(col.dataIndex, val),
        onBlur: () => {},
      }

      // Pass related field as dynamic prop if configured
      if (col.related && col.relatedProp && tempFilters[col.related]) {
        props[col.relatedProp] = tempFilters[col.related]
      }

      // Merge custom props if provided
      if (col.props && typeof col.props === 'object') {
        Object.assign(props, col.props)
      }

      return <Component {...props} />
    }

    switch (col.filterType) {
      case 'text':
        return (
          <Input
            value={tempFilters[col.dataIndex] || ''}
            onChange={e => handleChange(col.dataIndex, e.target.value)}
          />
        )
      case 'select':
        return (
          <Select
            value={tempFilters[col.dataIndex]}
            onChange={val => handleChange(col.dataIndex, val)}
            allowClear
            style={{ width: '100%' }}
          >
            {col.filterOptions?.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        )
      case 'date':
        return (
          <DatePicker
            style={{ width: '100%' }}
            value={
              tempFilters[col.dataIndex]
                ? dayjs(tempFilters[col.dataIndex])
                : null
            }
            onChange={(date, dateString) =>
              handleChange(col.dataIndex, dateString)
            }
          />
        )
      default:
        return (
          <Input
            value={tempFilters[col.dataIndex] || ''}
            onChange={e => handleChange(col.dataIndex, e.target.value)}
          />
        )
    }
  }

  const hasActiveFilters =
    search?.trim() !== '' || Object.keys(filters).length > 0

  return (
    <div className='flex items-center gap-2 customInput mb-4'>
      <Input
        type='text'
        placeholder='Search...'
        className='input input-bordered max-w-80'
        value={tempSearch}
        onChange={e => setTempSearch(e.target.value)}
        onPressEnter={handleSearch}
      />

      <button className='btn btn-outline-theme' onClick={handleSearch}>
        <i className='fas fa-search'></i>
      </button>

      <button
        className={`btn ${
          Object.keys(filters).length > 0 ? 'btn-outline-theme' : ''
        }`}
        onClick={() => setOpen(true)}
      >
        <i className='fas fa-filter'></i>
      </button>

      {hasActiveFilters && (
        <button className='btn btn-outline-danger' onClick={handleReset}>
          Reset
        </button>
      )}

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleApplyFilters}
        title='Advanced Filters'
      >
        <div className='flex flex-col gap-3'>
          {columns
            .filter(col => col.filter)
            .map(col => (
              <div key={col.dataIndex} className='flex flex-col gap-1'>
                {!col.filterComponent && (
                  <label className="form-label">{col.title}</label>
                )}
                {renderField(col)}
              </div>
            ))}
        </div>
      </Modal>
    </div>
  )
}

export default SearchAndFilterBar
